/**
 * DateRangePicker Component
 *
 * Unified date range picker for start date + due date with calendar,
 * time pickers, and preset date ranges.
 */

import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock, ArrowRight } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  addDays,
  isToday,
  isSameMonth,
  isSameDay,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  nextMonday,
  nextFriday,
  isBefore,
  isAfter,
} from 'date-fns';
import { it } from 'date-fns/locale';
import clsx from 'clsx';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useClickOutside } from '@/hooks/useClickOutside';

function normalizeDate(date: Date | number | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);
  return null;
}

// ─── Presets ─────────────────────────────────────────────────────────────────

interface DatePreset {
  label: string;
  getRange: () => { start: Date; end: Date };
}

function getPresets(): DatePreset[] {
  const now = new Date();
  return [
    {
      label: 'Oggi',
      getRange: () => ({ start: startOfDay(now), end: endOfDay(now) }),
    },
    {
      label: 'Domani',
      getRange: () => {
        const tomorrow = addDays(now, 1);
        return { start: startOfDay(tomorrow), end: endOfDay(tomorrow) };
      },
    },
    {
      label: 'Questa settimana',
      getRange: () => ({
        start: startOfDay(now),
        end: endOfDay(endOfWeek(now, { weekStartsOn: 1 })),
      }),
    },
    {
      label: 'Prossima settimana',
      getRange: () => {
        const mon = nextMonday(now);
        const fri = nextFriday(mon);
        return { start: startOfDay(mon), end: endOfDay(fri) };
      },
    },
    {
      label: 'Prossimi 3 giorni',
      getRange: () => ({
        start: startOfDay(now),
        end: endOfDay(addDays(now, 2)),
      }),
    },
    {
      label: 'Prossimi 7 giorni',
      getRange: () => ({
        start: startOfDay(now),
        end: endOfDay(addDays(now, 6)),
      }),
    },
  ];
}

// ─── Calendar range picker (internal) ────────────────────────────────────────

type SelectionPhase = 'start' | 'end';

interface CalendarRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChangeStart: (date: Date | null) => void;
  onChangeEnd: (date: Date | null) => void;
  showTime?: boolean;
  disabled?: boolean;
  onClose: () => void;
}

function CalendarRangePicker({
  startDate,
  endDate,
  onChangeStart,
  onChangeEnd,
  showTime = false,
  disabled = false,
  onClose,
}: CalendarRangePickerProps) {
  const [currentMonth, setCurrentMonth] = useState(() => startDate || endDate || new Date());
  const [phase, setPhase] = useState<SelectionPhase>('start');
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const [startTime, setStartTime] = useState(() =>
    startDate && showTime ? format(startDate, 'HH:mm') : '09:00'
  );
  const [endTime, setEndTime] = useState(() =>
    endDate && showTime ? format(endDate, 'HH:mm') : '18:00'
  );

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { locale: it });
  const calendarEnd = endOfWeek(monthEnd, { locale: it });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const applyTime = useCallback((date: Date, time: string): Date => {
    const d = new Date(date);
    if (showTime) {
      const [h, m] = time.split(':').map(Number);
      d.setHours(h, m, 0, 0);
    }
    return d;
  }, [showTime]);

  const handleDateClick = (day: Date) => {
    if (disabled) return;

    if (phase === 'start') {
      const d = applyTime(startOfDay(day), startTime);
      onChangeStart(d);
      // If end exists and new start is after end, clear end
      if (endDate && isAfter(d, endDate)) {
        onChangeEnd(null);
      }
      setPhase('end');
    } else {
      // Selecting end
      const d = applyTime(startOfDay(day), endTime);
      if (startDate && isBefore(d, startDate)) {
        // Clicked before start — swap: this becomes start, old start becomes end
        onChangeEnd(applyTime(startOfDay(startDate), endTime));
        onChangeStart(applyTime(startOfDay(day), startTime));
      } else {
        onChangeEnd(d);
      }
      setPhase('start');
    }
  };

  const handleStartTimeChange = (time: string) => {
    setStartTime(time);
    if (startDate) {
      const d = new Date(startDate);
      const [h, m] = time.split(':').map(Number);
      d.setHours(h, m, 0, 0);
      onChangeStart(d);
    }
  };

  const handleEndTimeChange = (time: string) => {
    setEndTime(time);
    if (endDate) {
      const d = new Date(endDate);
      const [h, m] = time.split(':').map(Number);
      d.setHours(h, m, 0, 0);
      onChangeEnd(d);
    }
  };

  const handlePreset = (preset: DatePreset) => {
    const { start, end } = preset.getRange();
    onChangeStart(applyTime(start, startTime));
    onChangeEnd(applyTime(end, endTime));
    setPhase('start');
  };

  const handleClear = () => {
    onChangeStart(null);
    onChangeEnd(null);
    setPhase('start');
  };

  const isInRange = (day: Date): boolean => {
    const s = startDate;
    const e = phase === 'end' && hoverDate ? hoverDate : endDate;
    if (!s || !e) return false;
    const rangeStart = isBefore(s, e) ? s : e;
    const rangeEnd = isAfter(s, e) ? s : e;
    return isAfter(day, rangeStart) && isBefore(day, rangeEnd);
  };

  const isRangeStart = (day: Date) => startDate && isSameDay(day, startDate);
  const isRangeEnd = (day: Date) => {
    const e = phase === 'end' && hoverDate ? hoverDate : endDate;
    return e && isSameDay(day, e);
  };

  const formatShort = (d: Date | null) =>
    d ? format(d, 'd MMM', { locale: it }) : '—';

  const presets = getPresets();

  return (
    <div className="bg-surface-primary rounded-lg border border-border-primary shadow-2xl flex overflow-hidden">
      {/* Presets sidebar */}
      <div className="w-[140px] border-r border-border-primary p-2 space-y-0.5 bg-surface-secondary/50">
        <p className="text-[9px] uppercase tracking-wider text-text-quaternary font-semibold px-2 py-1">
          Presets
        </p>
        {presets.map((p) => (
          <button
            type="button"
            key={p.label}
            onClick={() => handlePreset(p)}
            disabled={disabled}
            className="w-full text-left px-2 py-1.5 rounded-md text-[11px] text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
          >
            {p.label}
          </button>
        ))}
        <div className="pt-1 mt-1 border-t border-border-primary/50">
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="w-full text-left px-2 py-1.5 rounded-md text-[11px] text-accent-rose hover:bg-accent-rose/10 transition-colors"
          >
            Cancella date
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="p-3 min-w-[260px]">
        {/* Range summary */}
        <div className="flex items-center gap-2 mb-3 px-1">
          <button
            type="button"
            onClick={() => setPhase('start')}
            className={clsx(
              'flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium text-center border transition-colors',
              phase === 'start'
                ? 'border-accent-violet bg-accent-violet/10 text-accent-violet'
                : 'border-border-primary text-text-secondary hover:border-border-secondary'
            )}
          >
            <span className="text-[9px] uppercase tracking-wider block text-text-quaternary mb-0.5">Inizio</span>
            {formatShort(startDate)}
          </button>
          <ArrowRight className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
          <button
            type="button"
            onClick={() => setPhase('end')}
            className={clsx(
              'flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium text-center border transition-colors',
              phase === 'end'
                ? 'border-accent-violet bg-accent-violet/10 text-accent-violet'
                : 'border-border-primary text-text-secondary hover:border-border-secondary'
            )}
          >
            <span className="text-[9px] uppercase tracking-wider block text-text-quaternary mb-0.5">Scadenza</span>
            {formatShort(endDate)}
          </button>
        </div>

        {/* Month nav */}
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => setCurrentMonth((prev) => subMonths(prev, 1))}
            className="p-1 rounded hover:bg-surface-hover transition-colors"
            disabled={disabled}
          >
            <ChevronLeft className="w-4 h-4 text-text-secondary" />
          </button>
          <span className="text-[12px] font-semibold text-text-primary">
            {format(currentMonth, 'MMMM yyyy', { locale: it })}
          </span>
          <button
            type="button"
            onClick={() => setCurrentMonth((prev) => addMonths(prev, 1))}
            className="p-1 rounded hover:bg-surface-hover transition-colors"
            disabled={disabled}
          >
            <ChevronRight className="w-4 h-4 text-text-secondary" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((d, i) => (
            <div key={i} className="text-[10px] font-semibold text-text-quaternary text-center py-0.5">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0.5">
          {days.map((day, i) => {
            const inMonth = isSameMonth(day, currentMonth);
            const selected = isRangeStart(day) || isRangeEnd(day);
            const inRange = isInRange(day);
            const todayDate = isToday(day);

            return (
              <button
                type="button"
                key={i}
                onClick={() => handleDateClick(day)}
                onMouseEnter={() => phase === 'end' && setHoverDate(day)}
                onMouseLeave={() => setHoverDate(null)}
                disabled={disabled || !inMonth}
                className={clsx(
                  'aspect-square rounded-md text-[11px] font-medium transition-all relative',
                  !inMonth && 'opacity-20',
                  selected
                    ? 'bg-accent-violet text-white z-10'
                    : inRange
                      ? 'bg-accent-violet/15 text-accent-violet'
                      : todayDate
                        ? 'bg-accent-violet/10 text-accent-violet font-semibold'
                        : 'hover:bg-surface-hover text-text-secondary',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>

        {/* Time pickers */}
        {showTime && (
          <div className="mt-3 pt-3 border-t border-border-primary/50 flex items-center gap-3">
            <div className="flex items-center gap-1.5 flex-1">
              <Clock className="w-3 h-3 text-text-quaternary shrink-0" />
              <span className="text-[10px] text-text-quaternary">Inizio</span>
              <input
                type="time"
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                className="px-1.5 py-1 text-[11px] bg-surface-tertiary border border-border-primary rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet text-text-primary w-[72px]"
                disabled={disabled}
              />
            </div>
            <div className="flex items-center gap-1.5 flex-1">
              <Clock className="w-3 h-3 text-text-quaternary shrink-0" />
              <span className="text-[10px] text-text-quaternary">Fine</span>
              <input
                type="time"
                value={endTime}
                onChange={(e) => handleEndTimeChange(e.target.value)}
                className="px-1.5 py-1 text-[11px] bg-surface-tertiary border border-border-primary rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet text-text-primary w-[72px]"
                disabled={disabled}
              />
            </div>
          </div>
        )}

        {/* Done button */}
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-accent-violet text-white hover:bg-accent-violet/90 transition-colors"
          >
            Fatto
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Exported component ──────────────────────────────────────────────────────

export interface DateRangePickerProps {
  startDate: number | null;
  endDate: number | null;
  onChangeStart: (date: number | null) => void;
  onChangeEnd: (date: number | null) => void;
  showTime?: boolean;
  disabled?: boolean;
  highlightEnd?: boolean;
  /** Override trigger button className for different contexts (e.g. form inputs) */
  triggerClassName?: string;
}

export function DateRangePicker({
  startDate,
  endDate,
  onChangeStart,
  onChangeEnd,
  showTime = true,
  disabled = false,
  highlightEnd = false,
  triggerClassName,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 8,
    minWidth: 420,
    matchTriggerWidth: false,
  });

  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);

  useClickOutside([dropdownRef, triggerRef], () => setIsOpen(false), isOpen);

  const formatDisplay = (d: Date | null, fallback: string) => {
    if (!d) return fallback;
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      ...(showTime && { hour: '2-digit', minute: '2-digit' }),
    });
  };

  const hasAnyDate = start || end;

  if (disabled) {
    return (
      <div className="flex items-center gap-1.5 text-[10px]">
        <Calendar className="w-3 h-3 text-text-quaternary shrink-0" />
        <span className="text-text-tertiary">{formatDisplay(start, '—')}</span>
        <ArrowRight className="w-2.5 h-2.5 text-text-quaternary shrink-0" />
        <span className={highlightEnd ? 'text-accent-rose font-medium' : 'text-text-tertiary'}>
          {formatDisplay(end, '—')}
        </span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={triggerClassName || clsx(
          'flex items-center gap-1.5 rounded-md transition-all bg-surface-tertiary hover:bg-surface-hover px-2.5 h-6 text-[10px] whitespace-nowrap',
          hasAnyDate ? 'text-text-secondary' : 'text-text-quaternary'
        )}
        title="Clicca per modificare le date"
      >
        <Calendar className="w-3.5 h-3.5 shrink-0 text-text-quaternary" />
        <span>{formatDisplay(start, 'Inizio')}</span>
        <ArrowRight className="w-2.5 h-2.5 text-text-quaternary shrink-0" />
        <span className={highlightEnd ? 'text-accent-rose font-medium' : ''}>
          {formatDisplay(end, 'Scadenza')}
        </span>
      </button>

      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="z-50 animate-fade-in overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CalendarRangePicker
              startDate={start}
              endDate={end}
              onChangeStart={(d) => onChangeStart(d ? d.getTime() : null)}
              onChangeEnd={(d) => onChangeEnd(d ? d.getTime() : null)}
              showTime={showTime}
              onClose={() => setIsOpen(false)}
            />
          </div>,
          document.body
        )}
    </>
  );
}
